import React from 'react';
import { Plus } from 'lucide-react';

/**
 * Reusable GroupHeader component for collapsible log groups
 * Used across Maintenance, Fines, Salik, BatterySwaps, and Insurance pages
 * 
 * @param {string} title - Display title for the group
 * @param {number} count - Number of records in the group
 * @param {string} groupName - Internal group identifier
 * @param {boolean} isCollapsed - Whether the group is collapsed
 * @param {Function} onToggle - Function to call when toggling collapse state
 * @param {string} colorScheme - Color scheme for the group header (e.g., 'blue', 'purple', 'red')
 */
export const GroupHeader = ({ 
  title, 
  count, 
  groupName, 
  isCollapsed, 
  onToggle,
  colorScheme = 'slate' 
}) => {
  // Define color classes based on color scheme
  const colorClasses = {
    slate: {
      gradient: 'from-slate-50 to-slate-100',
      border: 'border-slate-200',
      hoverGradient: 'hover:from-slate-100 hover:to-slate-150',
      bg: 'bg-white',
      icon: 'text-slate-600'
    },
    blue: {
      gradient: 'from-blue-50 to-blue-100',
      border: 'border-blue-200',
      hoverGradient: 'hover:from-blue-100 hover:to-blue-150',
      bg: 'bg-white',
      icon: 'text-blue-600'
    },
    purple: {
      gradient: 'from-purple-50 to-purple-100',
      border: 'border-purple-200',
      hoverGradient: 'hover:from-purple-100 hover:to-purple-150',
      bg: 'bg-white',
      icon: 'text-purple-600'
    },
    red: {
      gradient: 'from-red-50 to-red-100',
      border: 'border-red-200',
      hoverGradient: 'hover:from-red-100 hover:to-red-150',
      bg: 'bg-white',
      icon: 'text-red-600'
    },
    green: {
      gradient: 'from-green-50 to-green-100',
      border: 'border-green-200',
      hoverGradient: 'hover:from-green-100 hover:to-green-150',
      bg: 'bg-white',
      icon: 'text-green-600'
    },
    orange: {
      gradient: 'from-orange-50 to-orange-100',
      border: 'border-orange-200',
      hoverGradient: 'hover:from-orange-100 hover:to-orange-150',
      bg: 'bg-white',
      icon: 'text-orange-600'
    }
  };

  const colors = colorClasses[colorScheme] || colorClasses.slate;

  return (
    <div 
      className={`flex items-center justify-between px-4 py-3 bg-gradient-to-r ${colors.gradient} border ${colors.border} cursor-pointer ${colors.hoverGradient} transition-all duration-200 group`}
      onClick={() => onToggle(groupName)}
    >
      <div className="flex items-center gap-3">
        <div className={`transition-transform duration-200 p-1 rounded-full ${colors.bg} shadow-sm group-hover:shadow-md ${isCollapsed ? 'rotate-0' : 'rotate-90'}`}>
          <Plus className={`w-3 h-3 ${colors.icon}`} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{count} {count === 1 ? 'record' : 'records'}</p>
        </div>
      </div>
      <div className="text-xs text-gray-400 font-medium">
        {isCollapsed ? 'Click to expand' : 'Click to collapse'}
      </div>
    </div>
  );
};
